// Expanded list of forbidden terms including profanity and policy violations.
export const FORBIDDEN_TERMS = [
  // Policy-related terms (High-severity, low false-positive rate)
  'illicit', 'foul', 'hate', 'scam', 'violence', 'illegal', 'fraud', 'terror', 'nft_scam', 'rugpull', 'darkweb', 'xxx', 'nsfw',

  // High-severity profanities and slurs (Keep for now, but long-term solution should be more nuanced)
  'arse', 'bastard', 'cock', 'cunt', 'dick', 'dike', 'fag', 'faggot', 'fuck', 'fucker', 'jizz', 'motherfucker', 'nigger', 'pussy', 'slut', 'twat', 'wanker', 'whore'

  // Removed common/mild profanities that cause false positives:
  // 'asshole', 'bitch', 'bollocks', 'bullshit', 'damn', 'goddamn', 'hell', 'piss', 'shit'
];

/**
 * Checks text content for forbidden terms using whole word matching to avoid false positives.
 * For example, it will flag 'ass' but not 'bass' or 'classic'.
 * Returns true if a violation is found.
 */
export const checkContentForViolation = (text: string): boolean => {
  if (!text) return false;
  
  // Create a regex pattern that matches any of the forbidden terms as whole words.
  // The \b is a word boundary assertion.
  // The 'i' flag makes the search case-insensitive.
  const pattern = new RegExp(`\\b(${FORBIDDEN_TERMS.join('|')})\\b`, 'i');
  
  return pattern.test(text);
};


export const MODERATION_WARNING_TEXT =
  'Zero Tolerance Policy: Content is monitored by AI. Use of illicit, foul, or hateful language/content (text, audio, video, NFT) will result in an IMMEDIATE ACCOUNT BLOCK subject to appeal.';

interface AnalysisResult {
    violation: boolean;
    reason: string | null;
}

// Simulates a more advanced content analysis for various file types.
export const analyzeFileContent = async (file: File): Promise<AnalysisResult> => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type;

    // Simulate a delay for a mock API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 1. Check filename for obvious violations
    if (checkContentForViolation(fileName)) {
        return { violation: true, reason: "Filename contains violative terms." };
    }

    // 2. Simulate specific checks based on file type
    if (fileType.startsWith('image/') || fileType.startsWith('video/')) {
        // In a real app, this would involve an API call to a service like Google Cloud Vision or Video Intelligence.
        // Here, we simulate by checking for keywords in the filename.
        if (fileName.includes('explicit') || fileName.includes('nsfw')) {
            return { violation: true, reason: `Simulated AI detected explicit content in ${fileType}.` };
        }
        if (fileName.includes('copyright')) {
            return { violation: true, reason: `Simulated AI detected copyrighted material in ${fileType}.` };
        }
    }

    // 3. For text-based files, you could read the content and analyze it.
    // This is omitted for brevity but would be similar to checkContentForViolation.

    return { violation: false, reason: null };
};
